import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { Coins, Gift, X, Sparkles } from 'lucide-react';
import BuyRunegoldModal from '../economy/BuyRunegoldModal';

export default function DonationButton({ 
  recipientId, 
  recipientName, 
  variant = 'default' // 'default' | 'reel' | 'compact'
}) {
  const { user, updateUser } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [sending, setSending] = useState(false);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [buyReason, setBuyReason] = useState('');

  if (user?.id === recipientId) return null;

  const handleDonate = async () => {
    const donationAmount = parseInt(amount);
    
    if (!donationAmount || donationAmount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (donationAmount > (user?.wonder_coins || 0)) {
      setBuyReason(`You need ${donationAmount - (user?.wonder_coins || 0)} more Runegold to tip ${recipientName || 'this creator'}.`);
      setShowBuyModal(true);
      return;
    }

    setSending(true);
    try {
      const response = await api.post('/wallet/donate', {
        recipient_id: recipientId,
        amount: donationAmount
      });
      
      toast.success(`Tipped ${donationAmount} Runegold to ${recipientName}!`);
      updateUser({ wonder_coins: response.data.new_balance });
      setIsOpen(false);
      setAmount('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send tip');
    } finally {
      setSending(false);
    }
  };

  const quickAmounts = [5, 10, 25, 50];

  const buyModal = (
    <BuyRunegoldModal
      open={showBuyModal}
      onOpenChange={setShowBuyModal}
      reason={buyReason}
    />
  );

  // Reel variant
  if (variant === 'reel') {
    return (
      <div className="relative">
        {isOpen ? (
          <div className="absolute right-0 bottom-0 flex flex-col items-end gap-2 animate-in slide-in-from-bottom-2">
            <button onClick={() => setIsOpen(false)} className="w-8 h-8 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
              <X className="w-4 h-4 text-white" />
            </button>
            <div className="flex flex-col gap-1">
              {quickAmounts.map(amt => (
                <button
                  key={amt}
                  onClick={() => setAmount(amt.toString())}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    amount === amt.toString() ? 'bg-yellow-500 text-black' : 'bg-white/20 backdrop-blur text-white hover:bg-white/30'
                  }`}
                >
                  {amt} <Coins className="w-3 h-3 inline" />
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1 bg-white/20 backdrop-blur rounded-full px-2 py-1">
              <Input
                type="number" value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="..."
                className="w-14 h-7 text-xs bg-transparent border-none text-white text-center p-0"
              />
              <button onClick={handleDonate} disabled={sending || !amount} className="w-7 h-7 rounded-full bg-yellow-500 flex items-center justify-center disabled:opacity-50">
                <Sparkles className="w-4 h-4 text-black" />
              </button>
            </div>
          </div>
        ) : (
          <button onClick={() => setIsOpen(true)} className="flex flex-col items-center gap-0.5" data-testid="donate-btn">
            <div className="w-11 h-11 rounded-full bg-yellow-500/20 backdrop-blur-sm flex items-center justify-center hover:bg-yellow-500/40 transition-colors">
              <Gift className="w-5 h-5 text-yellow-500" />
            </div>
            <span className="text-white text-[10px]">Tip</span>
          </button>
        )}
        {buyModal}
      </div>
    );
  }

  // Compact variant (post cards)
  if (variant === 'compact') {
    return (
      <div className="relative">
        {isOpen ? (
          <div className="absolute bottom-full right-0 mb-2 p-3 bg-card border border-[hsl(var(--mg-rune)/0.4)] rounded-xl shadow-lg min-w-[200px] animate-in slide-in-from-bottom-2 z-50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium flex items-center gap-1.5">
                <Coins className="w-4 h-4 text-primary" />
                Tip Runegold
              </span>
              <button onClick={() => setIsOpen(false)} className="text-muted-foreground hover:text-foreground" aria-label="Close tip panel">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex gap-1 mb-2">
              {quickAmounts.map(amt => (
                <button key={amt} onClick={() => setAmount(amt.toString())}
                  className={`flex-1 py-1 rounded text-xs font-medium transition-all ${
                    amount === amt.toString() ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'
                  }`}
                >{amt}</button>
              ))}
            </div>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Coins className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-primary" />
                <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount" className="pl-8 h-8 text-sm" min="1" />
              </div>
              <Button onClick={handleDonate} disabled={sending || !amount} size="sm" className="h-8">
                {sending ? '...' : 'Tip'}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Balance: {user?.wonder_coins || 0} Runegold
            </p>
          </div>
        ) : (
          <button onClick={() => setIsOpen(true)} className="mg-coin-tip" data-testid="donate-btn" aria-label={`Tip ${recipientName || 'creator'} Runegold`}>
            <Coins className="w-4 h-4" />
            <span>Tip</span>
          </button>
        )}
        {buyModal}
      </div>
    );
  }

  // Default variant
  return (
    <div className="relative">
      {isOpen ? (
        <div className="p-4 bg-card border border-border rounded-xl shadow-lg animate-in slide-in-from-bottom-2">
          <div className="flex items-center justify-between mb-3">
            <span className="font-medium">Tip Runegold</span>
            <button onClick={() => setIsOpen(false)} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
          </div>
          <p className="text-sm text-muted-foreground mb-3">
            Send a tip to <span className="text-foreground font-medium">{recipientName}</span>
          </p>
          <div className="flex gap-2 mb-3">
            {quickAmounts.map(amt => (
              <button key={amt} onClick={() => setAmount(amt.toString())}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                  amount === amt.toString() ? 'bg-yellow-500 text-black' : 'bg-muted hover:bg-muted/80'
                }`}
              >{amt} <Coins className="w-3 h-3 inline ml-1" /></button>
            ))}
          </div>
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <Coins className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-yellow-500" />
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Enter amount" className="pl-10" min="1" />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Balance: <span className="text-yellow-500 font-medium">{user?.wonder_coins || 0} Runegold</span>
            </p>
            <Button onClick={handleDonate} disabled={sending || !amount} className="bg-yellow-500 hover:bg-yellow-600 text-black">
              {sending ? 'Sending...' : `Send ${amount || 0}`}
            </Button>
          </div>
        </div>
      ) : (
        <Button onClick={() => setIsOpen(true)} variant="outline" className="border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10" data-testid="donate-btn">
          <Gift className="w-4 h-4 mr-2" /> Tip Runegold
        </Button>
      )}
      {buyModal}
    </div>
  );
}
